import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Button } from 'primeng/button';
import { Select } from 'primeng/select';
import { Toolbar } from 'primeng/toolbar';
import { MessageService } from 'primeng/api';
import { LogService } from '@wheere-front/shared-util';
import { ReadFileRequest } from '@wheere/api-gateway';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { GatewaysV7Store } from '../data/gateways-v7.store';

interface FileChoice {
  name: string;
  value: string;
  partition: string;
}

const FILE_CHOICES: FileChoice[] = [
  { name: 'logs', value: 'logs.txt', partition: 'log' },
  { name: 'logs bak', value: 'logs_bak.txt', partition: 'log' },
  { name: 'signal', value: 'signal.json', partition: 'user' },
  { name: 'attenuator', value: 'att.json', partition: 'user' },
  { name: 'clock', value: 'clock.json', partition: 'user' },
  { name: 'clock input', value: 'clock_input.json', partition: 'user' },
  { name: 'fpga ok', value: 'fpga_ok.txt', partition: 'user' },
  { name: 'settings', value: 'settings.json', partition: 'user' },
];

/**
 * V7 Gateway "Read file" component — recreated from inside `GatewayReadFileComponent`.
 *
 * <p>Replaces the integrator's previous free-form input with a PrimeNG select listing the
 * known files (logs, signal, attenuator, clock, …) and their partitions. Sending the command
 * POSTs to {@code /gateways/{id}/commands/read-file} via {@link GatewaysV7Store#readFile} and
 * the gateway streams the file content back over the WebSocket (
 * {@code /topic/gateway/{id}/file-content} + {@code /topic/gateway/{id}/file-status}). Lines
 * accumulate in {@link fileLines} and render in a terminal-style panel.
 */
@Component({
  selector: 'lib-gateway-read-file-selector',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule, Button, Select, Toolbar, TranslocoModule],
  template: `
    <div class="flex flex-col gap-3">
      <p-toolbar class="w-full">
        <ng-template #start>
          <div class="flex items-center gap-2">
            <p-select
              [options]="fileChoices"
              [(ngModel)]="selectedFile"
              optionLabel="name"
              appendTo="body"
              [style]="{ width: '12rem' }">
            </p-select>
            <p-button
              [label]="'gatewayReadFile.toolbar.sendCommand' | transloco"
              icon="pi pi-send"
              size="small"
              [loading]="sending()"
              (click)="onSend()">
            </p-button>
            <span class="ml-2 inline-flex items-center gap-1.5 text-xs font-semibold text-gray-700">
              <span
                class="h-2 w-2 rounded-full"
                [class.bg-green-500]="fileStatus() === 'received' || fileStatus() === 'streaming'"
                [class.bg-yellow-400]="fileStatus() === 'sent' || fileStatus() === 'pending'"
                [class.bg-gray-400]="fileStatus() === 'idle'"></span>
              {{ 'gatewayReadFile.status.' + fileStatus() | transloco }}
            </span>
          </div>
        </ng-template>
        <ng-template #end>
          <div class="flex gap-2">
            <p-button
              [label]="'gatewayReadFile.toolbar.clear' | transloco"
              icon="pi pi-trash"
              size="small"
              severity="danger"
              [disabled]="!fileLines().length"
              (click)="onClear()">
            </p-button>
            <p-button
              [label]="'gatewayReadFile.toolbar.download' | transloco"
              icon="pi pi-download"
              size="small"
              severity="secondary"
              [disabled]="!fileLines().length"
              (click)="onDownload()">
            </p-button>
          </div>
        </ng-template>
      </p-toolbar>

      <div class="terminal">
        @if (formattedLines().length === 0) {
          <div class="flex h-full items-center justify-center text-gray-500">
            {{ 'gatewayReadFile.empty.before' | transloco
            }}<b class="ml-1 mr-1">{{ 'gatewayReadFile.empty.cta' | transloco }}</b>
            {{ 'gatewayReadFile.empty.after' | transloco }}
          </div>
        } @else {
          @for (html of formattedLines(); track $index) {
            <div class="terminal-line" [innerHTML]="html"></div>
          }
        }
      </div>
    </div>
  `,
  styles: [
    `
      .terminal {
        background: #0f0f0f;
        color: #e5e7eb;
        font-family: Consolas, 'Courier New', Courier, monospace;
        font-size: 12px;
        line-height: 1.5;
        border-radius: 8px;
        padding: 12px 14px;
        height: 50vh;
        overflow: auto;
        white-space: pre-wrap;
        word-break: break-all;
      }
      .terminal-line {
        white-space: pre-wrap;
      }
      /* Same colour palette as gateway-logs-live for the spans LogService injects. */
      :host ::ng-deep .terminal-line .timestamp {
        color: #9ca3af;
      }
      :host ::ng-deep .terminal-line .brackets {
        color: #6b7280;
      }
    `,
  ],
})
export class GatewayReadFileSelectorComponent {
  gatewayId = input.required<string>();
  /** Optional callback invoked when the user clicks Download — caller saves the file. */
  deviceId = input<string>('');

  protected readonly fileChoices = FILE_CHOICES;
  protected selectedFile: FileChoice = FILE_CHOICES[0];

  protected readonly sending = signal(false);

  #store = inject(GatewaysV7Store);
  /** Read-through to the store — file-content + file-status topics are subscribed at the
   * page level, so switching tabs while a file is streaming doesn't lose data. */
  protected readonly fileLines = this.#store.liveFileContent;
  /** Default to 'idle' when the store hasn't received a status frame yet. */
  protected readonly fileStatus = computed(() => this.#store.fileStatus() ?? 'idle');

  /**
   * ANSI-coloured rendering of {@link fileLines}. The gateway emits log files that contain
   * embedded escape sequences ([32m[INF][0m etc.) — {@code LogService.formatLogLineRaw}
   * turns them into coloured spans so the level tags render properly, exactly like the
   * live logs panel.
   */
  protected readonly formattedLines = computed<SafeHtml[]>(() =>
    this.fileLines().map(line => this.#sanitizer.bypassSecurityTrustHtml(this.#logService.formatLogLineRaw(line)))
  );

  #messageService = inject(MessageService);
  #logService = inject(LogService);
  #sanitizer = inject(DomSanitizer);
  #transloco = inject(TranslocoService);

  protected async onSend(): Promise<void> {
    const id = this.gatewayId();
    if (!id) return;
    this.sending.set(true);
    // Reset the store buffers so the previous file's content doesn't bleed into the new
    // stream — the page-level WS sub will refill liveFileContent as the gateway responds.
    this.#store.clearFileContent();
    this.#store.setFileStatus('pending');
    try {
      const body: ReadFileRequest = {
        filename: this.selectedFile.value,
        partition: this.selectedFile.partition,
        last: -1,
      } as ReadFileRequest;
      await this.#store.readFile(id, this.selectedFile.value, this.selectedFile.partition);
      this.#store.setFileStatus('sent');
      this.#messageService.add({
        severity: 'info',
        key: 'save',
        detail: this.#transloco.translate('gatewayReadFile.toasts.commandSent', { name: this.selectedFile.name }),
      });
      void body;
    } catch (e) {
      this.#store.setFileStatus('idle');
      this.#messageService.add({
        severity: 'error',
        key: 'save',
        detail: this.#transloco.translate('gatewayReadFile.toasts.commandFailed'),
      });
    } finally {
      this.sending.set(false);
    }
  }

  protected onClear(): void {
    this.#store.clearFileContent();
    this.#store.setFileStatus('idle');
  }

  protected onDownload(): void {
    const blob = new Blob([this.fileLines().join('\n')], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const stamp = new Date().toISOString().replace(/[:.]/g, '-');
    a.download = `${this.deviceId() || this.gatewayId()}-${this.selectedFile.value}-${stamp}.txt`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }
}
